import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as appHelpers from '@src/services/helpers/appHelpers';

import { SocialNetworkType } from '@src/components/SocialNetworkButton';
import { Browsing, QsNames } from '@src/types/Browsing';
import { useQueryMedia } from '@src/queries/media/useQueryMedia';
import { useQueryGroupMentionPages } from '@src/queries/users/useQueryGroupMentionPages';
import { ArrayHelper } from '@src/services/helpers/array';
import { useAtomValue } from 'jotai';
import { useMutationRightsRequestCreate } from '@src/queries/media/useMutationMediaRightsRequestCreate';
import { useMutationRightsRequestUpdate } from '@src/queries/media/useMutationMediaRightsRequestUpdate';
import { useMutationUserRightsTemplate } from '@src/queries/users/useMutationUserRightsTemplate';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useQueryUserRightsTemplate } from '@src/queries/users/useQueryUserRightsTemplate';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import { ServiceName, Services } from '@src/types/Services';
import { RightRequestStatus } from '@adalong/schemas';
import { ButtonTarget, GetRights } from './GetRights';
import { goToCloseGetRights } from '../utils/mediaRoute';
import { EPopup } from '@src/types/common';
import { processTemplateContent } from '@src/utils/crmTemplates';
import { popupsAtom, rightsTemplateFrAtom, rightsSelectedTemplateAtom, setRightsTemplateFr, setRightsSelectedTemplate, setPopup } from '@src/state/popupAtoms';

const CREATOR_TAG = '@[creator]';

interface Props {
  services: Services
}

export default function GetRightsController(props: Props) {
  const rightsTemplateFr = useAtomValue(rightsTemplateFrAtom);
  const { data: mentionPagesList } = useQueryGroupMentionPages();
  const mentionPages = useMemo(
    () => ArrayHelper.mapToObject(mentionPagesList || [], (p) => p.id),
    [mentionPagesList],
  );
  const selectedTemplate = useAtomValue(rightsSelectedTemplateAtom);
  const getRightsPopup = useAtomValue(popupsAtom)[EPopup.getRights];

  // Get mediaId from popup atom
  const mediaId = getRightsPopup && 'mediaId' in getRightsPopup ? getRightsPopup.mediaId : 
                  getRightsPopup && 'medias' in getRightsPopup && getRightsPopup.medias.length > 0 ? getRightsPopup.medias[0]._id : 
                  null;

  // Don't render if no popup state or no mediaId
  if (!getRightsPopup || !mediaId) {
    return null;
  }

  const [{
    browsingService,
  }] = useState(() => ({
    browsingService: props.services.get<Browsing>(ServiceName.Browsing),
  }));

  const [maxChar, setMaxChar] = useState(0);
  const [remainingChar, setRemainingChar] = useState(0);
  const [postOpened, setPostOpened] = useState(false);
  const [errorCustom, setErrorCustom] = useState<{
    type: string
    data?: any
  } | null
  >(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const finalCommentRef = useRef<HTMLTextAreaElement>(null);

  const { data: media } = useQueryMedia(mediaId);
  const { data: rightsTemplate } = useQueryUserRightsTemplate();
  const { data: userConfig } = useQueryUserConfig();
  const oauth = userConfig?.user?.oauth;

  const {
    mutateAsync: createRightsRequest,
    isPending: isCreating,
    error: errorCreating,
    reset: resetCreating,
  } = useMutationRightsRequestCreate();
  const {
    mutateAsync: updateRightsRequest,
    isPending: isUpdating,
    error: errorUpdating,
    reset: resetUpdating,
  } = useMutationRightsRequestUpdate();
  const { mutateAsync: updateRightsTemplate } = useMutationUserRightsTemplate();

  const [hashtag, setHashtag] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [templateWarnings, setTemplateWarnings] = useState<string[]>([]);
  
  // Template selection state (selectedTemplate from atom)
  const [mode, setMode] = useState<'initial' | 'template' | 'manual'>('initial');

  const crmEnabled = userConfig?.user?.groupFeatures?.crm || false;
  useEffect(() => {
    if (!crmEnabled) {
      setMode('manual');
    }
  }, [crmEnabled]);

  useEffect(() => {
    if (!rightsTemplate) {
      return;
    }
    const finalComment = getfinalComment(comment);
    const max = getMaxChar(finalComment, userConfig?.config?.rightstemplate.maxComment);
    const maxCharOffset = finalComment.length - comment.length;
    // Due to the difference between the CREATOR_TAG and the true creator name
    // we need to compensate the number of character for the textarea maxLength
    setMaxChar(max.char - maxCharOffset);
    setRemainingChar(max.remaining);
  }, [rightsTemplate, hashtag, comment, media?.source.user_name]);
  useEffect(() => {
    setComment(
      (rightsTemplateFr ? rightsTemplate?.template.commentFR : rightsTemplate?.template.comment) || '',
    );
  }, [rightsTemplateFr]);

  // Listen for template selection from atom (from global TemplateSelector)
  useEffect(() => {
    if (selectedTemplate) {
      const { processedContent, warnings } = processTemplateContent({
        content: selectedTemplate.content || '',
        currentTemplate: selectedTemplate,
        termsUrl: selectedTemplate?.terms || userConfig?.user?.group?.terms_url
      });
      setComment(processedContent);
      setHashtag(selectedTemplate.hashtag || '');
      setMode('template');
      setTemplateWarnings(warnings);
    }
  }, [selectedTemplate, userConfig]);

  useEffect(() => {
    setHashtag(rightsTemplate?.template.hashtag || '');
    setComment(
      (rightsTemplateFr ? rightsTemplate?.template.commentFR : rightsTemplate?.template.comment) || '',
    );
  }, [rightsTemplate]);

  const social = media?.source.name as SocialNetworkType;

  // Are mentions in user's group mention pages
  const mentions = !!(media?.mentions || []).find((id: string) => !!mentionPages[id.split('_')[1]]);

  /*
    Work around: Until July 1st 2020 the FB graph_id was badly stored.
    Thus, it's not possible to use the API to post a comment to request rights.
    --> The user must post the RR manually
    */
  const isOldMedia: boolean = media ? new Date(media?.published_at) < new Date(2020, 6, 1) : false;

  const mustConfirm = social === 'instagram' && (!mentions || isOldMedia);
  const error = errorCustom || errorCreating || errorUpdating;
  const loading = isCreating || isUpdating;

  // Template selection functions
  const openTemplateSelector = () => {
    setPopup(EPopup.templateSelector, { filterType: 'RR' });
  };

  const closeTemplateSelector = () => {
    setPopup(EPopup.templateSelector, null);
  };

  const switchToManualMode = () => {
    setMode('manual');
    setTemplateWarnings([]);
    setRightsSelectedTemplate(null);
    setComment(
      (rightsTemplateFr ? rightsTemplate?.template.commentFR : rightsTemplate?.template.comment) || '',
    );
    setHashtag(rightsTemplate?.template.hashtag || '');
  };

  const switchToTemplateMode = () => {
    setPopup(EPopup.templateSelector, { filterType: 'RR' });
  };

  const changeTemplate = () => {
    setPopup(EPopup.templateSelector, { filterType: 'RR' });
  };

  return (
    <GetRights
      media={media}
        hashtag={hashtag}
        comment={comment}
        finalComment={getfinalComment(comment)}
        missingSocial={isMissingSocial(social, oauth)}
        rightsTemplate={rightsTemplate}
        french={rightsTemplateFr || false}
        commentChar={{
          max: maxChar,
          remaining: remainingChar,
        }}
        loading={loading}
        error={error}
        confirmButtonVariant={getConfirmButtonVariant(mustConfirm, postOpened)}
        textAreaRef={textAreaRef}
        finalCommentRef={finalCommentRef}
        mode={mode}
        selectedTemplate={selectedTemplate || null}
        templateWarnings={templateWarnings}
        crmEnabled={crmEnabled}
        handlers={{
          copyText,
          termsClick,
          setFrench,
          openPostClick,
          hashtagChange: setHashtag,
          commentChange: setComment,
          sendClick: handleSend,
          doneClick: handleDone,
          close,
          openTemplateSelector,
          switchToManualMode,
          switchToTemplateMode,
          changeTemplate,
        }}
      />
  );

  function handleSend(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    e.preventDefault();
    if (remainingChar < 0) {
      return setErrorCustom({ type: 'maxlength' });
    }
    if (!mediaId) {
      return setErrorCustom({ type: 'missing_media' });
    }
    const message = prepareCommentForPublishing(comment);
    if (message === null) {
      return;
    }
    setErrorCustom(null);
    resetCreating();

    createRightsRequest({
      mediaId,
      rightsRequest: {
        message,
        replyTag: hashtag,
        status: RightRequestStatus.Created,
      },
    }).then(() => {
      close();
      appHelpers.addAlert({ text: lang.success_done, type: 'success' });
    });
  }

  function handleDone(e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
    e.preventDefault();
    if (!mediaId) {
      return setErrorCustom({ type: 'missing_media' });
    }
    const message = prepareCommentForPublishing(comment);
    if (message === null) {
      return;
    }
    setErrorCustom(null);
    resetUpdating();
    updateRightsRequest({
      mediaId,
      rightsRequest: {
        message,
        replyTag: hashtag,
        status: RightRequestStatus.Published,
      },
    }).then(() => {
      close();
      appHelpers.addAlert({ text: lang.success_done, type: 'success' });
    });
  }

  async function close(): Promise<void> {
    persistRightsTemplate();
    // Close popup (query string will be handled by GetRightsQueryStringDetector)
    setPopup(EPopup.getRights, null);
    browsingService.goToPage(goToCloseGetRights(window.location));
  }

  /**
     * save rights template to be reused later
     */
  async function persistRightsTemplate(): Promise<unknown> {
    const doc = {
      ...rightsTemplate?.template,
      hashtag: hashtag.replace(/#|\s/g, ''),
      [rightsTemplateFr ? 'commentFR' : 'comment']: comment.trim(),
    };
    await updateRightsTemplate({
      rightsTemplate: doc,
    });
    return doc;
  }

  async function setFrench(french: boolean): Promise<void> {
    await persistRightsTemplate();
    setRightsTemplateFr(french);
  }

  /**
   * Insert hashtag, terms or creator in the comment
   */
  function copyText(target: ButtonTarget) {
    const node = textAreaRef.current;
    if (node) {
      const start = node.selectionStart;
      const end = node.selectionEnd;
      let replaceBy: string;
      switch (target) {
        case 'hashtag':
          replaceBy = hashtag ? `#${hashtag}` : '#';
          break;
        case 'terms':
          // Priority: selected template terms, then group terms
          replaceBy = selectedTemplate?.terms || rightsTemplate?.terms || '';
          break;
        case 'creator':
          replaceBy = media?.source.user_name ? CREATOR_TAG : '@';
          break;
        default:
          throw new Error('No button selected');
      }
      setComment(comment.slice(0, start || undefined) + replaceBy + (end ? comment.slice(end) : ''));
    }
  }

  /**
   * Return the publishable comment
   * If creator is not known, it displays an alert and return null
   */
  function prepareCommentForPublishing(cmt: string): string | null {
    try {
      const creator = media?.source.user_name;
      return formatComment(cmt, { creator });
    } catch (e) {
      appHelpers.addAlert({ text: lang.creator_missing, type: 'danger' });
      return null;
    }
  }

  /**
   * Return a comment as if it was ready to publish
   */
  function getfinalComment(cmt: string): string {
    try {
      const creator = media?.source.user_name;
      return formatComment(cmt, { creator });
    } catch (e) {
      return cmt;
    }
  }

  /**
   * Format the comment by:
   * - Replacing the creator tag by the creator name
   * - Replacing URL and terms placeholders if still present
   *
   * It returns the new string or throw a custom error
   */
  function formatComment(cmt: string, data: { creator: string | undefined }): string {
    let formattedComment = cmt;
    
    // Replace creator tag
    const creatorTagPresent = formattedComment.indexOf(CREATOR_TAG) !== -1;
    if (creatorTagPresent) {
      if (typeof data.creator !== 'string') {
        throw new Error(lang.creator_missing);
      }
      // replace all occurences
      formattedComment = formattedComment.split(CREATOR_TAG).join(`@${data.creator}`);
    }
    
    // Replace URL placeholders if still present
    const websiteUrl = selectedTemplate?.url || '';
    formattedComment = formattedComment.replace(/\[url\]/g, websiteUrl);
    
    // Replace terms placeholders if still present
    const termsUrl = selectedTemplate?.terms || userConfig?.user?.group?.terms_url || '';
    formattedComment = formattedComment.replace(/\[terms\]/g, termsUrl);
    
    // Replace hashtag placeholders if still present
    const hashtagValue = selectedTemplate?.hashtag || hashtag || '';
    formattedComment = formattedComment.replace(/#\[hashtag\]/g, hashtagValue ? `#${hashtagValue}` : '');
    
    return formattedComment;
  }

  function termsClick() {
    // Priority: selected template terms, then group terms
    const link = selectedTemplate?.terms || rightsTemplate?.terms;
    if (link) {
      window.open(link, '_blank');
    }
  }

  /**
   * Force to copy the final version of the comment
   * And open the instagram post
   */
  function openPostClick(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault();
    setPostOpened(true);
    // We try to prepare to comment to know if there's a format error
    // If so, the function called will handle it and we simply return to stop
    // opening the post
    const message = prepareCommentForPublishing(comment);
    if (message === null) {
      return;
    }
    // we use finalCommentRef to get the comment with the creator name replaced if necessary
    const textToCopy = finalCommentRef.current;
    if (textToCopy) {
      textToCopy.select();
    }
    document.execCommand('copy');
    window.open(media?.url.post, '_blank');
  }
}

function getConfirmButtonVariant(
  mustConfirm: boolean,
  postOpened: boolean,
) {
  if (mustConfirm) {
    if (postOpened) {
      return 'done';
    }
    return 'openPost';
  }
  return 'send';
}

function getMaxChar(comment?: string, maxComment: number = 280) {
  return {
    char: maxComment,
    remaining: maxComment - (comment || '').length,
  };
}

function isMissingSocial(
  social: SocialNetworkType,
  oauth: { twitter?: unknown; facebook?: unknown; instagram?: unknown } | undefined,
) {
  switch (social) {
    case 'twitter':
      return !oauth?.twitter;
    case 'instagram':
      // We don't need user tokens for FB/IG mentions or instagram manual requests
      return false;
    default:
      return false;
  }
}

const lang = LocalizedStrings({
  en: {
    success_done: 'Request saved, it will be processed soon',
    creator_missing: `A creator tag is present but the creator name is not available. Please remove the ${CREATOR_TAG} tag`,
  },
});
